/* eslint-disable camelcase */
// Field names below are snake_case on purpose: they are the GA4 / GTM dataLayer contract
// (GA4 ecommerce uses item_name, transaction_id, etc.). Renaming them to camelCase would
// break the operator's GTM tags, so the camelcase lint rule is disabled for this file.

/**
 * Analytics events — GTM dataLayer contract.
 *
 * FerryHawk's job is to push clean, semantically-named events with rich metadata onto
 * `window.dataLayer`. Operators wire those events to their own destinations (GA4, Google
 * Ads, Meta Pixel/CAPI, Brevo, ...) inside their own GTM container — no FerryHawk code
 * change needed to add/modify/remove a tag. See google-analytics-events-plan.md.
 *
 * THE EVENT NAMES AND PARAM KEYS BELOW ARE A PUBLIC CONTRACT. Renaming `value`, `route`,
 * an event name, etc. silently breaks the operator's GTM tags. Treat changes as breaking.
 *
 * Event names follow GA4 recommended ecommerce names so a single push maps cleanly to
 * both GA4 and Meta standard events.
 *
 * Locally these pushes land in `window.dataLayer` (a plain array) and are logged to the
 * console in development, so the full funnel can be verified without any GTM/GA4 account.
 */

import { store } from 'Models/Store';
import paymentStore from 'Models/PaymentStore';

/** A single purchasable line in the booking (vehicle, trailer, passenger ticket, add-on). */
export interface AnalyticsItem {
	/** Human-readable name, e.g. "Vehicle - Standard", "Adult passenger". */
	item_name: string;
	/** Coarse grouping: 'vehicle' | 'trailer' | 'passenger' | 'addon' | string. */
	item_category?: string;
	/** Per-unit price in dollars (numeric, not a formatted string). */
	price?: number;
	quantity?: number;
}

/** Metadata shared across booking funnel events. All optional — push what's in scope. */
export interface BookingEventData {
	/** Booking/cart total in dollars (numeric). */
	value?: number;
	/** ISO currency code. Defaults to the configured payment currency (`paymentStore.currencyCode`). */
	currency?: string;
	/** Route as origin→destination codes/labels, e.g. "TSV-NEL". */
	route?: string;
	/** Sailing/departure date, ISO 'YYYY-MM-DD'. */
	sailing_date?: string;
	/** Stable booking reference — required on `purchase` for Enhanced Conversions / CAPI dedupe. */
	transaction_id?: string;
	/** Line-item breakdown (vehicle/passenger/add-ons). */
	items?: AnalyticsItem[];
	/** Any extra route/trip context the operator may want to segment on. */
	[key: string]: unknown;
}

/** Normalise a display name to a snake_case analytics value, e.g. "Uncover Resident" -> "uncover_resident". */
function toUserTypeSlug(value: string): string {
	return value
		.trim()
		.toLowerCase()
		.replace(/[^a-z0-9]+/g, '_')
		.replace(/^_+|_+$/g, '');
}

/**
 * Explicit user type, sourced from the auth store and attached to every event so operators can
 * segment by who triggered it. Most-specific wins. A call site can still override `user_type`.
 *
 * Order: not-logged-in -> `visitor`; logged-in guest checkout -> `guest`; staff hierarchy ->
 * role; a customer with a configured display name (resident/business/government/etc.) -> that
 * name slugified; otherwise a plain logged-in customer -> `customer`.
 */
export function getUserType(): string {
	if (!store.loggedIn) {
		return 'visitor';
	}
	if (store.userData?.isGuest) {
		return 'guest';
	}
	if (store.isSuper) {
		return 'super_admin';
	}
	if (store.isAdmin) {
		return 'admin';
	}
	if (store.isManager) {
		return 'manager';
	}
	if (store.isStaff) {
		return 'staff';
	}
	// Customer segment (e.g. resident / business / government) from their configured display name.
	const displayName = store.userData?.userDisplayName?.displayName;
	if (displayName) {
		return toUserTypeSlug(displayName);
	}
	if (store.isInvoicedUser) {
		return 'invoiced';
	}
	if (store.isFixedPriceUser) {
		return 'fixed_price';
	}
	return 'customer';
}

/**
 * Core push. Defensively initialises `window.dataLayer` so events are captured even when
 * the GTM snippet hasn't loaded (e.g. analytics disabled locally), keeping the funnel
 * fully inspectable in the console. Every event is stamped with an explicit `user_type`.
 */
export function pushDataLayerEvent(event: string, data: Record<string, unknown> = {}): void {
	if (typeof window === 'undefined') {
		return;
	}
	// Correlate events within a single booking flow: the ferry booking transaction id lives in
	// sessionStorage from reservation through to the success page (it's the same id `purchase`
	// reports). Attach it when present so the funnel can be traced end-to-end. Explicit `data`
	// (e.g. purchase's own transaction_id) still wins. Key mirrors BookingService.ts.
	const transactionId = window.sessionStorage?.getItem('ferry-booking-transaction-id-key');
	const payload = {
		event,
		user_type: getUserType(),
		...(transactionId ? { transaction_id: transactionId } : {}),
		...data,
	};
	window.dataLayer = window.dataLayer || [];
	window.dataLayer.push(payload);

	if (process.env.NODE_ENV === 'development') {
		// eslint-disable-next-line no-console
		console.debug('[dataLayer]', event, payload);
	}
}

/** User searched/viewed available sailings for a route + date. GA4 `view_item_list` / Meta `Search`. */
export const trackSearchTrips = (data: BookingEventData = {}) =>
	pushDataLayerEvent('view_item_list', { currency: paymentStore.currencyCode, ...data });

/** User selected a specific sailing. GA4 `select_item` / Meta `ViewContent`. */
export const trackSelectTrip = (data: BookingEventData = {}) =>
	pushDataLayerEvent('select_item', { currency: paymentStore.currencyCode, ...data });

/** Vehicle/passenger details added. GA4 `add_to_cart` / Meta `AddToCart`. */
export const trackAddToCart = (data: BookingEventData = {}) =>
	pushDataLayerEvent('add_to_cart', { currency: paymentStore.currencyCode, ...data });

/** Booking wizard / checkout started. GA4 `begin_checkout` / Meta `InitiateCheckout`. */
export const trackBeginCheckout = (data: BookingEventData = {}) =>
	pushDataLayerEvent('begin_checkout', { currency: paymentStore.currencyCode, ...data });

/** Reached the payment step. GA4 `add_payment_info` / Meta `AddPaymentInfo`. */
export const trackAddPaymentInfo = (data: BookingEventData = {}) =>
	pushDataLayerEvent('add_payment_info', { currency: paymentStore.currencyCode, ...data });

/** Booking confirmed — the key conversion. GA4 `purchase` / Meta `Purchase`. */
export const trackPurchase = (data: BookingEventData = {}) =>
	pushDataLayerEvent('purchase', { currency: paymentStore.currencyCode, ...data });

// TODO: not yet wired to a call site — uncomment and call from the notify-me / waitlist action.
// /** Notify-me / standby waitlist registration. GA4 `generate_lead` / Meta `Lead`. */
// export const trackNotifyMe = (data: BookingEventData = {}) =>
// 	pushDataLayerEvent('generate_lead', { ...data });

/** Removed a line from the cart. GA4 `remove_from_cart` / Meta `RemoveFromCart`. */
export const trackRemoveFromCart = (data: BookingEventData = {}) =>
	pushDataLayerEvent('remove_from_cart', { currency: paymentStore.currencyCode, ...data });

// --- Non-ecommerce funnel/engagement events (custom names, mapped in GTM) ---

/** Viewed the ferry schedule / timetable page. */
export const trackViewFerrySchedule = (data: BookingEventData = {}) =>
	pushDataLayerEvent('view_ferry_schedule', { ...data });

/** Clicked "Book" on a sailing from the ferry schedule. */
export const trackBookFromSchedule = (data: BookingEventData = {}) =>
	pushDataLayerEvent('book_from_schedule', { ...data });

/**
 * Started a guided booking from the homepage by type. Emits a distinct event per type:
 * `start_guided_vehicle_booking`, `start_guided_walk_on_booking`, `start_guided_freight_booking`.
 */
export const trackStartGuidedBooking = (bookingType: 'vehicle' | 'walk_on' | 'freight', data: BookingEventData = {}) =>
	pushDataLayerEvent(`start_guided_${bookingType}_booking`, { booking_type: bookingType, ...data });

/** Submitted a trip search in the guided experience, with the chosen search parameters. */
export const trackTripSearch = (data: BookingEventData = {}) => pushDataLayerEvent('search_trips', { ...data });

/** Started editing/altering an existing booking. */
export const trackStartEditBooking = (data: BookingEventData = {}) =>
	pushDataLayerEvent('start_edit_booking', { ...data });

/** Clicked "Add another booking" from the cart to add a further booking to the same flow. */
export const trackAddAnotherBooking = (data: BookingEventData = {}) =>
	pushDataLayerEvent('add_another_booking', { ...data });

/** User logged in. GA4 `login`. */
export const trackLogin = (data: BookingEventData = {}) => pushDataLayerEvent('login', { ...data });

/** User registered a new account. GA4 `sign_up`. */
export const trackSignUp = (data: BookingEventData = {}) => pushDataLayerEvent('sign_up', { ...data });

/** User verified/activated their account. */
export const trackVerifiedAccount = (data: BookingEventData = {}) =>
	pushDataLayerEvent('verified_account', { ...data });

/** User requested a password reset from the login flow. */
export const trackForgotPassword = (data: BookingEventData = {}) => pushDataLayerEvent('forgot_password', { ...data });
